/**
 * Reduce _commits_ so that there's only one per day, selecting the latest
 * commit for each day.
 *
 * @param { Array<{}> } commits An array of commits in a document.
 * @returns { typeof commits }
 */
const reduceCommitsToDaily = commits =>
  commits.reduce(
    (acc, cur) => {
      const date = convertCommitDateToEditDate(cur.commitDate);
      if (!acc.seen[date]) {
        acc.commits.push(cur);
        acc.seen[date] = true;
      } else {
        acc.commits[acc.commits.length - 1] = cur;
      }
      return acc;
    },
    {
      seen: {},
      commits: [],
    },
  ).commits;

/**
 * Convert a Unix timestamp in MS (commitDate) into an _edit date_ string.
 *
 * @param { number } commitDate An integer, a Unix timestamp in milliseconds
 * @returns { string } an edit date
 * @example '2022-10-25'
 */
const convertCommitDateToEditDate = commitDate => {
  commitDate = new Date(commitDate);
  return `${commitDate.getFullYear()}-${commitDate.getMonth() + 1}-${commitDate.getDate()}`;
};

/**
 * Optional chaining `dailyCommits[i-1]?.data[field]` would do the same if it was
 * available. TODO: Add polyfill?
 */
const getEarlierValue = (commits, curIdx, dataField) => {
  if (curIdx - 1 < 0) return undefined;
  return commits[curIdx - 1].data[dataField];
};

// `'none'` seems to be rendered as `'-'`, whereas `undefined` is not rendered at all
const stringifyUndefinedValue = (value, stringRepresentation = 'none') => {
  return value === undefined ? stringRepresentation : value;
};

/**
 * Transform _commits_ into _edits_.
 *
 * @param { Array<{}> } commits An array of commits in a document.
 * @returns { { [ date: string ]: Array<Record<'field' | 'time' | 'from' | 'to', unknown>> } } Record of edits per date.
 * @example { '2022-10-25': [{ field: 'familyPrevalence', time: 1666704528090, from: 'yes', to: 'no' }] }
 */
const transformCommitsIntoEdits = commits => {
  const edits = {};
  const dailyCommits = reduceCommitsToDaily(commits);
  for (let i = 0; i < dailyCommits.length; i++) {
    const editDate = convertCommitDateToEditDate(dailyCommits[i].commitDate);
    edits[editDate] = Object.entries(dailyCommits[i].data).map(([field, to]) => {
      return {
        field,
        time: dailyCommits[i].commitDate,
        from: stringifyUndefinedValue(getEarlierValue(dailyCommits, i, field)),
        to,
      };
    });
  }

  return edits;
};

export default transformCommitsIntoEdits;
